import {
  BoundSelectField,
  Button,
  Css,
  IconButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  Tag,
  useModal,
} from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { Observer, observer } from "mobx-react";
import { ReactNode } from "react";
import { Maybe, SavePlanPackageInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageOptionsV2_OptionFragment,
  PlanPackageOptionsV2_PlanPackageFragment,
  PlanPackageOptionsV2Query,
  useSavePlanPackageOptionsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";
import {
  ImpactRow,
  impactsConfig,
  ProgramDataImpactsSection,
  toImpactRows,
  toProgramDataInputs,
} from "./ProgramDataImpactsSection";

type EnumDetails = PlanPackageOptionsV2Query["enumDetails"];

type EditOptionModalProps = {
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  option: PlanPackageOptionsV2_OptionFragment;
  enumDetails: EnumDetails;
};

type EditOptionForm = {
  readyPlanLocationId: Maybe<string>;
  optionPrerequisiteIds: string[];
  /** The combined conflicts, i.e. `optionConflicts` and `optionConflictChildren`, since they are bidirectional. */
  conflictIds: string[];
  impacts: ImpactRow[];
};
type FormState = ObjectState<EditOptionForm>;

export function EditOptionModal(props: EditOptionModalProps) {
  const { planPackage, option, enumDetails } = props;
  const { closeModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageOptionsV2Mutation();
  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const formState = useFormState({
    config: formConfig,
    init: { input: option, map: (o) => mapToForm(o, planPackage) },
  });

  async function onSave() {
    const input = toSaveInput(formState, option, planPackage);
    if (input.options || input.programDatas) {
      const { data } = await save({ variables: { input } });
      const versionId = data?.savePlanPackage.planPackage.version.id;
      // Saving on a published version forks a new draft, so navigate to it
      if (versionId && versionId !== planPackage.version.id) navigateToRpav(versionId);
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>
        <div css={Css.xs.gray700.$}>{option.code}</div>
        <div css={Css.xl.pt1.$}>{option.name}</div>
        <Tag text={option.type.name} />
      </ModalHeader>
      <Observer>
        {() => (
          <>
            <ModalBody>
              <div css={Css.df.fdc.gap3.pt2.$}>
                {option.description && <div>{option.description}</div>}
                {option.type.allowsLocations && (
                  <LocationSection
                    planPackage={planPackage}
                    option={option}
                    formState={formState}
                    disabled={disabled}
                  />
                )}
                <RelatedOptionsSection
                  title="Requirements"
                  subtitle="Add an option that must be selected first before this option can be selected."
                  placeholder="Add a Requirement"
                  field={formState.optionPrerequisiteIds}
                  options={eligiblePrerequisites(option, planPackage.options, formState)}
                  allOptions={planPackage.options}
                  disabled={disabled}
                />
                <RelatedOptionsSection
                  title="Conflicts"
                  subtitle="Add an option that cannot be combined with this option."
                  placeholder="Add a Conflict"
                  field={formState.conflictIds}
                  options={eligibleConflicts(option, planPackage.options, formState)}
                  allOptions={planPackage.options}
                  disabled={disabled}
                />
                <div>
                  <SectionHeader
                    title="Program Data Impact"
                    subtitle="Indicate how this option changes program data like square footage or bed/bath count."
                  />
                  <ProgramDataImpactsSection
                    option={option}
                    planPackage={planPackage}
                    enumDetails={enumDetails}
                    impacts={formState.impacts}
                    disabled={disabled}
                  />
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button label="Cancel" variant="tertiary" onClick={closeModal} />
              <Button label="Save" onClick={onSave} disabled={disabled || !formState.dirty} />
            </ModalFooter>
          </>
        )}
      </Observer>
    </>
  );
}

function LocationSection(props: {
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  option: PlanPackageOptionsV2_OptionFragment;
  formState: FormState;
  disabled: ReactNode | boolean;
}) {
  const { planPackage, option, formState, disabled } = props;
  // Moving an option also moves its whole group, so it is only allowed for single-option groups
  const groupSiblings = planPackage.options.filter(
    (o) => o.id !== option.id && o.optionGroup.id === option.optionGroup.id,
  );
  const sameOptionSiblings = planPackage.options.filter(
    (o) => o.id !== option.id && o.globalOption.id === option.globalOption.id,
  );
  return (
    <BoundSelectField
      label="Location"
      field={formState.readyPlanLocationId}
      options={planPackage.roomLocations}
      unsetLabel="None"
      fullWidth
      disabled={
        disabled || (groupSiblings.nonEmpty && "Options that share an option group must stay in the same location")
      }
      disabledOptions={sameOptionSiblings
        .map((o) => o.readyPlanLocation?.id)
        .compact()
        .map((value) => ({ value, reason: `Another ${option.name} is already assigned to this location` }))}
    />
  );
}

type RelatedOptionsSectionProps = {
  title: string;
  subtitle: string;
  placeholder: string;
  field: FieldState<string[]>;
  options: PlanPackageOptionsV2_OptionFragment[];
  allOptions: PlanPackageOptionsV2_OptionFragment[];
  disabled: ReactNode | boolean;
};

/** A list of related options (requirements or conflicts) as removable rows plus an "add" select. */
const RelatedOptionsSection = observer(function RelatedOptionsSection(props: RelatedOptionsSectionProps) {
  const { title, subtitle, placeholder, field, options, allOptions, disabled } = props;
  const selected = (field.value ?? []).map((id) => allOptions.find((o) => o.id === id)).compact();
  return (
    <div>
      <SectionHeader title={title} subtitle={subtitle} />
      <div css={Css.df.fdc.gap1.$}>
        {selected.map((o) => (
          <div key={o.id} css={Css.df.aic.gap1.ba.bcGray300.br4.pxPx(12).pyPx(6).$}>
            <span css={Css.xs.gray700.$}>{o.code}</span>
            <span css={Css.smSb.$}>{o.name}</span>
            <span css={Css.xs.gray700.mla.$}>{o.readyPlanLocation?.name ?? "None"}</span>
            <IconButton
              icon="x"
              disabled={disabled}
              onClick={() => field.set(field.value!.filter((id) => id !== o.id))}
            />
          </div>
        ))}
        <SelectField
          label={title}
          fullWidth
          labelStyle="hidden"
          placeholder={placeholder}
          value={undefined as string | undefined}
          options={options}
          getOptionLabel={(o) => `${o.code} - ${o.name}${o.readyPlanLocation ? ` (${o.readyPlanLocation.name})` : ""}`}
          getOptionValue={(o) => o.id}
          onSelect={(id) => id && field.set([...(field.value ?? []), id])}
          disabled={disabled}
        />
      </div>
    </div>
  );
});

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div css={Css.pb1.$}>
      <h2 css={Css.mdSb.$}>{title}</h2>
      <div css={Css.sm.gray700.pt1.$}>{subtitle}</div>
    </div>
  );
}

const formConfig: ObjectConfig<EditOptionForm> = {
  readyPlanLocationId: { type: "value" },
  optionPrerequisiteIds: { type: "value" },
  conflictIds: { type: "value" },
  impacts: { type: "list", config: impactsConfig },
};

function mapToForm(
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
): EditOptionForm {
  return {
    readyPlanLocationId: option.readyPlanLocation?.id,
    optionPrerequisiteIds: option.optionPrerequisites.map((o) => o.id),
    conflictIds: [...option.optionConflicts, ...option.optionConflictChildren].uniqueBy((o) => o.id).map((o) => o.id),
    impacts: toImpactRows(option, planPackage),
  };
}

function toSaveInput(
  formState: FormState,
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
): SavePlanPackageInput {
  const { readyPlanLocationId, optionPrerequisiteIds, conflictIds, impacts } = formState;
  const optionInput = {
    id: option.id,
    ...(readyPlanLocationId.dirty && { readyPlanLocationId: readyPlanLocationId.value ?? null }),
    ...(optionPrerequisiteIds.dirty && { optionPrerequisiteIds: optionPrerequisiteIds.value }),
    // Conflicts are bidirectional but stored one-way, so removals must apply to whichever side holds the edge,
    // while additions always become our outgoing conflicts
    ...(conflictIds.dirty && {
      optionConflictIds: conflictIds.value.filter((id) => !option.optionConflictChildren.some((o) => o.id === id)),
      optionConflictChildIds: option.optionConflictChildren
        .map((o) => o.id)
        .filter((id) => conflictIds.value.includes(id)),
    }),
  };
  const programDatas = impacts.dirty ? toProgramDataInputs(option, planPackage, formState.impacts.value) : [];
  return {
    id: planPackage.id,
    ...(Object.keys(optionInput).length > 1 && { options: [optionInput] }),
    ...(programDatas.nonEmpty && { programDatas }),
  };
}

/** Options this option could require */
function eligiblePrerequisites(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  formState: FormState,
) {
  const conflictIds = formState.conflictIds.value ?? [];
  const prereqIds = formState.optionPrerequisiteIds.value ?? [];
  return (
    allOptions
      .filter((o) => o.id !== option.id)
      .filter((o) => o.optionGroup.id !== option.optionGroup.id)
      .filter((o) => !prereqIds.includes(o.id))
      .filter((o) => !conflictIds.includes(o.id))
      // Avoid cycles, i.e. the candidate cannot (transitively) require this option
      .filter((o) => !transitivePrerequisiteIds(o, allOptions).includes(option.id))
      .sortBy((o) => o.name)
  );
}

/** Options this option could conflict with. */
function eligibleConflicts(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  formState: FormState,
) {
  const conflictIds = formState.conflictIds.value ?? [];
  const prereqIds = formState.optionPrerequisiteIds.value ?? [];
  return allOptions
    .filter((o) => o.id !== option.id)
    .filter((o) => o.optionGroup.id !== option.optionGroup.id)
    .filter((o) => !conflictIds.includes(o.id))
    .filter((o) => !prereqIds.includes(o.id))
    .sortBy((o) => o.name);
}

function transitivePrerequisiteIds(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  visited: Set<string> = new Set(),
): string[] {
  return option.optionPrerequisites.flatMap(({ id }) => {
    if (visited.has(id)) return [];
    visited.add(id);
    const prereq = allOptions.find((o) => o.id === id);
    return [id, ...(prereq ? transitivePrerequisiteIds(prereq, allOptions, visited) : [])];
  });
}
